import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DeviceMobile, ShieldCheck, Prohibit, ArrowClockwise,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import { API_URL, formatApiError } from '../../utils/api';
import { DataTable } from '../../components/DataTable';
import { Input } from '../../components/ui/input';

/**
 * Live list of every user currently signed into the EcoFront mobile app.
 * Super-admin only (backend enforces).
 *
 * Backend: GET /api/admin/ecofront/app-users
 * Actions: force sign-out (revoke refresh token)
 */
function timeAgo(iso) {
  try {
    const ts = new Date(iso).getTime();
    if (!ts) return '';
    const s = Math.floor((Date.now() - ts) / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch (e) { return ''; }
}

const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function AppUsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/ecofront/app-users`, {
        withCredentials: true, headers: authHeaders(),
      });
      setUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load app users'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const forceSignOut = useCallback(async (row) => {
    if (!window.confirm(
      `Force sign-out ${row.fullname || row.mobile}?\n\n` +
      "Their phone will be signed out within seconds. They must log in again " +
      "with phone + password — PIN alone will not work."
    )) return;
    setRevoking(row.user_id);
    try {
      await axios.post(
        `${API_URL}/api/admin/ecofront/app-users/${row.user_id}/revoke`,
        {}, { withCredentials: true, headers: authHeaders() },
      );
      toast.success(`${row.fullname || 'User'} signed out`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to revoke session'));
    } finally {
      setRevoking(null);
    }
  }, [load]);

  const renderActionCell = useCallback((row) => (
    <button
      onClick={(e) => { e.stopPropagation(); forceSignOut(row); }}
      disabled={revoking === row.user_id}
      className="inline-flex items-center gap-1 h-8 px-2.5 text-[11px] font-semibold rounded-lg bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 disabled:opacity-50 transition-colors"
      data-testid={`app-users-revoke-${row.user_id}`}
      title="Force sign-out"
    >
      <Prohibit size={12} weight="fill" />
      {revoking === row.user_id ? 'Signing out…' : 'Sign out'}
    </button>
  ), [forceSignOut, revoking]);

  const search = q.trim().toLowerCase();
  const filtered = useMemo(() => (
    !search ? users : users.filter((u) =>
      (u.fullname || '').toLowerCase().includes(search) ||
      (u.mobile || '').toLowerCase().includes(search) ||
      (u.entity_name || '').toLowerCase().includes(search) ||
      (u.campus_name || '').toLowerCase().includes(search) ||
      (u.device_name || '').toLowerCase().includes(search),
    )
  ), [users, search]);

  return (
    <div className="space-y-4" data-testid="ecofront-app-users-tab">
      {/* Header row — search + refresh + summary */}
      <div className="flex items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, phone, entity, campus, device…"
            className="pl-9 border-[#EDE6D8] rounded-lg"
            data-testid="app-users-search"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#78716C]" data-testid="app-users-count">
            {filtered.length} active {filtered.length === 1 ? 'session' : 'sessions'}
          </span>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 h-9 px-3 text-xs font-semibold rounded-lg border border-[#EDE6D8] bg-white text-[#1C1917] hover:bg-[#FCFAF6] disabled:opacity-50 transition-colors"
            data-testid="app-users-refresh"
          >
            <ArrowClockwise size={14} weight="bold" />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={[
          {
            key: 'user',
            label: 'User',
            width: 'minmax(200px,1.6fr)',
            render: (r) => (
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-[#1C1917] group-hover:text-[#1C1917]"
                      style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {r.fullname || '—'}
                </span>
                <span className="block truncate text-[10px] text-[#78716C] group-hover:text-[#78716C] font-mono">
                  {r.mobile || ''}{r.role ? ` · ${r.role}` : ''}
                </span>
              </span>
            ),
          },
          {
            key: 'org',
            label: 'Entity · Campus',
            width: 'minmax(180px,1.4fr)',
            tone: 'muted',
            render: (r) => (
              <span className="min-w-0">
                <span className="block truncate text-sm text-[#1C1917] group-hover:text-[#1C1917]">
                  {r.entity_name || '—'}
                </span>
                <span className="block truncate text-[10px] text-[#78716C] group-hover:text-[#78716C]">
                  {r.campus_name || '—'}
                </span>
              </span>
            ),
          },
          {
            key: 'device',
            label: 'Device',
            width: 'minmax(160px,1.3fr)',
            render: (r) => (
              <span className="min-w-0 inline-flex items-center gap-2">
                <DeviceMobile size={14} weight="duotone" className="text-[#0A2540]/60 shrink-0" />
                <span className="min-w-0">
                  <span className="block truncate text-sm text-[#1C1917] group-hover:text-[#1C1917]"
                        style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {r.device_name || 'Unknown'}
                  </span>
                  <span className="block truncate text-[10px] text-[#78716C] group-hover:text-[#78716C] font-mono">
                    {r.device_id || '—'}
                  </span>
                </span>
              </span>
            ),
          },
          {
            key: 'pin',
            label: 'PIN',
            width: 'minmax(110px,0.9fr)',
            render: (r) => r.pin_enabled ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold tracking-wide"
                    data-testid={`pin-on-${r.user_id}`}>
                <ShieldCheck size={11} weight="fill" />
                ON
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#FCFAF6] text-[#78716C] text-[10px] font-semibold tracking-wide border border-[#EDE6D8]"
                    data-testid={`pin-off-${r.user_id}`}>
                OFF
              </span>
            ),
          },
          {
            key: 'signed_in',
            label: 'Signed In',
            width: 'minmax(110px,0.9fr)',
            tone: 'muted',
            render: (r) => (
              <span className="min-w-0">
                <span className="block truncate text-sm text-[#1C1917] group-hover:text-[#1C1917]"
                      style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {timeAgo(r.signed_in_at) || '—'}
                </span>
                <span className="block truncate text-[10px] text-[#78716C] group-hover:text-[#78716C] font-mono">
                  {r.signed_in_at?.slice(0, 16).replace('T', ' ') || ''}
                </span>
              </span>
            ),
          },
          {
            key: 'expires',
            label: 'Expires',
            width: 'minmax(110px,0.9fr)',
            tone: 'muted',
            render: (r) => r.expires_at?.slice(0, 10) || '—',
          },
        ]}
        rows={filtered}
        loading={loading}
        rowKey={(r) => r.user_id}
        testIdPrefix="app-users"
        emptyIcon={<DeviceMobile size={32} className="mx-auto text-[#A8A29E] mb-2" weight="duotone" />}
        emptyTitle={search ? 'No matching users' : 'No active app users'}
        emptyText={search
          ? 'Try a different search term.'
          : 'No cashier is currently signed into the EcoFront mobile app.'}
        actionRender={renderActionCell}
      />
    </div>
  );
}
